import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { useTranslation } from 'react-i18next';
import { executeHttpCommandBridge, getHttpBridgeResponseCommand } from '../../Utils/httpCommandBridge';

// Custom hook for component-specific WebSocket handling
export const useComponentWebSocket = (commandHandlers = {}) => {
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const commandHandlersRef = useRef(commandHandlers);

  useEffect(() => {
    commandHandlersRef.current = commandHandlers;
  }, [commandHandlers]);
  
  // Setup component-specific WebSocket message handling
  useEffect(() => {
    if (!ws) return () => {};
    
    const handleMessage = (parsedMessage) => {
      const command = parsedMessage.command;
      const currentHandlers = commandHandlersRef.current;
      if (command && currentHandlers[command]) {
        currentHandlers[command](parsedMessage);
      }
    };
    
    const unsubscribe = subscribeToMessages(handleMessage);
    return () => unsubscribe();
  }, [ws, subscribeToMessages]);
  
  // Send WebSocket command with data
  const sendCommand = useCallback(async (command, data = {}) => {
    const bridgedCommands = new Set([
      'getcases',
      'getcase',
      'saveTemplate',
      'getAllTemplates',
      'refreshSharedTemplateStatuses',
      'getSharedTemplates',
      'loadTemplate',
      'shareTemplate',
      'getSharedTemplateModeration',
      'verifySharedTemplate',
      'deleteSharedTemplate',
      'importSharedTemplate',
      'deleteTemplate',
      'importTemplate',
      'importTemplates',
      'createProjectFromTemplate',
      'get_project_tags',
      'get_all_unique_tags',
      'get_saved_models',
      'get_text_model_settings',
      'get_vision_model_settings',
      'get_audio_model_settings',
      'get_performance_settings',
      'get_format_settings',
      'get_available_ollama_models',
      'get_login_activity',
      'get_active_tasks_info',
      'get_postprocessing_status',
      'get_logs',
      'get_postprocessing_retries',
      'get_service_statuses',
      'control_service',
      'get_global_ai_token_usage',
      'get_server_info',
      'configuration_check',
      'update_configuration',
    ]);

    if (bridgedCommands.has(command)) {
      try {
        const bridgedMessage = await executeHttpCommandBridge({ command, data });
        const currentHandlers = commandHandlersRef.current;
        if (bridgedMessage && currentHandlers[bridgedMessage.command]) {
          currentHandlers[bridgedMessage.command](bridgedMessage);
        }
      } catch (error) {
        const currentHandlers = commandHandlersRef.current;
        const responseCommand = getHttpBridgeResponseCommand(command);
        if (currentHandlers[responseCommand]) {
          currentHandlers[responseCommand]({
            command: responseCommand,
            data: {
              status: 'error',
              message: error?.message || 'Request failed',
            },
          });
        } else {
          console.error(`HTTP bridge failed for ${command}:`, error);
        }
      }
      return;
    }

    if (typeof sendMessage === 'function') {
      sendMessage({ command, data });
      return;
    }

    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command, data }));
      return;
    }

    console.error('WebSocket is not open');
  }, [sendMessage, ws]);
  
  return { sendCommand, ws };
};

export const useGlobalSettingState = () => {
  const [isModified, setIsModified] = useState(false);
  const [credit, setCredit] = useState(0);
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();

  // Shared state that might be used across multiple components
  const [configurationCompleted, setConfigurationCompleted] = useState(false);
  const [hasVisitedBefore, setHasVisitedBefore] = useState(false);

  useEffect(() => {
    // Check if the component has been visited before
    const visitedBefore = localStorage.getItem('globalSettingVisited');
    if (visitedBefore === 'true') {
      setHasVisitedBefore(true);
    } else {
      // Set flag that user has visited this page
      localStorage.setItem('globalSettingVisited', 'true');
    }

    if (typeof sendMessage === 'function') {
      sendMessage({ command: 'configuration_check' });
    }

    const handleMessage = (parsedMessage) => {
      switch (parsedMessage.command) {
        case 'configuration_check':
          setConfigurationCompleted(parsedMessage.data === '1');
          break;
        case 'configuration_data':
          setCredit(parseInt(parsedMessage.data.credits, 0) || 0);
          break;
        default:
          break;
      }
    };

    const unsubscribe = subscribeToMessages(handleMessage);

    return () => unsubscribe();
  }, [sendMessage, subscribeToMessages]);

  const saveConfiguration = (configData) => {
    if (typeof sendMessage === 'function' && isModified) {
      sendMessage({
        command: 'update_configuration',
        data: configData
      });
      console.log('Configuration data sent:', configData);
    } else {
      console.error('Unable to send configuration update or no changes were made.');
    }
  };

  return {
    isModified,
    setIsModified,
    credit,
    setCredit,
    configurationCompleted,
    hasVisitedBefore,
    saveConfiguration,
    t,
    openSnackbar,
    ws,
    subscribeToMessages
  };
};

export const textModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold', label: 'DataUnfold', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'dataunfold_widget', label: 'DataUnfold Widget', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'openai', label: 'OpenAI', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'meta', label: 'Meta (Muse Spark / Wonder)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'groq', label: 'Groq', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'azure', label: 'Azure OpenAI (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#757575' },
  { value: 'azure_claude', label: 'Azure Claude (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure_grok', label: 'Azure Grok (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'gemini', label: 'Gemini (Google)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'anthropic', label: 'Claude (Anthropic)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'deepseek', label: 'DeepSeek', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'ollama', label: 'DeepSeek (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_qwen_vl', label: 'Qwen VL (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'aws_nova', label: 'Amazon Nova (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'aws_claude', label: 'Claude (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'vllm', label: 'vLLM (OpenAI Compatible)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' },
  { value: 'mistral', label: 'Mistral', accuracy: '★★★☆☆', accuracyColor: '#FF9800' },
  { value: 'openrouter', label: 'OpenRouter', accuracy: '★★★★★', accuracyColor: '#4CAF50' }
];

export const visionModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold', label: 'DataUnfold', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'openai', label: 'OpenAI', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'meta', label: 'Meta (Muse Spark / Wonder)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'groq', label: 'Groq', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'azure', label: 'Azure OpenAI (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#757575' },
  { value: 'azure_claude', label: 'Azure Claude (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure_grok', label: 'Azure Grok (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'gemini', label: 'Gemini (Google)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'anthropic', label: 'Claude (Anthropic)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'ollama_qwen_vl', label: 'Qwen VL (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'aws_nova', label: 'Amazon Nova (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'aws_claude', label: 'Claude (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'vllm', label: 'vLLM (OpenAI Compatible)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' },
  { value: 'mistral', label: 'Mistral', accuracy: '★★★☆☆', accuracyColor: '#FF9800' },
  { value: 'openrouter', label: 'OpenRouter', accuracy: '★★★★★', accuracyColor: '#4CAF50' }
];

export const decisionModelOptions = [
  { value: 'jev', label: 'Jev (TypeSafe)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'openai_decision', label: 'OpenAI Decisions (GPT-6 Luna)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'azure_decision', label: 'Microsoft Foundry (Microsoft-Decision-1)', accuracy: 'N/A', accuracyColor: '#757575' },
  ...textModelOptions
];

export const nativeDecisionProviders = new Set(['jev', 'openai_decision', 'azure_decision']);

export const nativeDecisionInputKey = (provider) => provider === 'jev'
  ? 'global_setting.jev_input_description' : 'global_setting.native_decision_input_description';

export const getProviderModelDefaults = (provider, modelType) => ({
  model_name: ({ jev: 'jev-latest', meta: 'muse-spark-1.3', openai_decision: 'gpt-6-luna' })[provider] || '',
  api_endpoint: ({
    jev: 'https://api.typesafe.ai/v1/systemone', meta: 'https://api.meta.ai/v1',
    openai_decision: 'https://api.openai.com/v1/decisions'
  })[provider] || '',
  max_tokens: modelType === 'decision' && provider !== 'meta' ? 512 : 60000,
  temperature: provider === 'meta' ? 1 : 0.1,
  reasoning_effort: provider === 'meta' ? 'medium' : 'none',
  logprobs_enabled: provider !== 'meta' && !nativeDecisionProviders.has(provider),
  max_context_tokens: provider === 'meta' ? 980000 : nativeDecisionProviders.has(provider) ? 32000 : 128000,
});

export const audioModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'openai', label: 'OpenAI Audio', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'dataunfold', label: 'DataUnfold Audio', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure', label: 'Azure OpenAI Audio', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'groq', label: 'Groq Whisper', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'vllm', label: 'vLLM (OpenAI Compatible)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' }
];

// Models that do not support model editing
export const modelWithoutModelEditing = ['dataunfold_mock'];

export const resourceModels = ['azure', 'azure_claude', 'azure_grok', 'local', 'vllm'];
export const noTokenTextModels = ['dataunfold', 'dataunfold_widget', 'ollama', 'ollama_qwen_vl', 'ollama_generic', 'vllm', 'none'];
export const noTokenVisionModels = ['dataunfold', 'ollama_qwen_vl', 'ollama_generic', 'vllm', 'none'];
export const noTokenAudioModels = ['dataunfold', 'ollama_generic', 'vllm', 'none'];
export const displayModelName = ['ollama_generic'];

// Providers that support advanced tuning (max_tokens, temperature)
export const tunableProviders = new Set([
  'meta',
  'openai',
  // Azure OpenAI exposes reasoning_effort for supported reasoning deployments.
  // Unsupported Azure models are handled by the provider retry fallback.
  'azure',
  'anthropic',
  'azure_claude',
  'azure_grok',
  'groq',
  'mistral',
  'deepseek',
  'aws_claude',
  'aws_nova',
  'gemini',
  'dataunfold',
  'dataunfold_widget',
  'vllm',
  'openrouter'
]);

// Shared provider icons and small descriptions used in dropdowns
export const providerIcons = {
  'openai_decision': { icon: `/images/ai/openai.png`, description: 'OpenAI Decisions', color: '#00A67E' },
  'azure_decision': { icon: `/images/ai/azure.png`, description: 'Microsoft Foundry Decisions', color: '#0078D4' },
  'meta': { description: 'Meta (Muse Spark / Wonder)', color: '#0866FF' },
  'none': { icon: `/images/ai/none.png`, description: 'Disabled', color: '#757575' },
  'openai': { icon: `/images/ai/openai.png`, description: 'OpenAI', color: '#00A67E' },
  'groq': { icon: `/images/ai/groq.png`, description: 'Groq', color: '#F97316' },
  'azure': { icon: `/images/ai/azure.png`, description: 'Azure OpenAI', color: '#0078D4' },
  'azure_claude': { icon: `/images/ai/azure.png`, description: 'Azure Claude', color: '#0078D4' },
  'azure_grok': { icon: `/images/ai/azure.png`, description: 'Azure Grok', color: '#0078D4' },
  'gemini': { icon: `/images/ai/gemini.png`, description: 'Gemini (Google)', color: '#4285F4' },
  'anthropic': { icon: `/images/ai/claude.png`, description: 'Anthropic (Claude)', color: '#D2691E' },
  'deepseek': { icon: `/images/ai/deepseek.png`, description: 'DeepSeek', color: '#6366F1' },
  'dataunfold': { icon: `/images/ai/dataunfold.png`, description: 'DataUnfold', color: '#5757b0' },
  'dataunfold_widget': { icon: `/images/ai/dataunfold.png`, description: 'DataUnfold Widget', color: '#5757b0' },
  'ollama': { icon: `/images/ai/ollama.png`, description: 'Ollama', color: '#10B981' },
  'ollama_qwen_vl': { icon: `/images/ai/ollama.png`, description: 'Qwen VL (Ollama)', color: '#10B981' },
  'ollama_generic': { icon: `/images/ai/ollama.png`, description: 'Local Ollama', color: '#10B981' },
  'aws_nova': { icon: `/images/ai/nova.png`, description: 'AWS Nova', color: '#FF9900' },
  'aws_claude': { icon: `/images/ai/claude.png`, description: 'AWS Claude', color: '#232F3E' },
  'local': { icon: `/images/ai/local.png`, description: 'Local Model', color: '#666666' },
  'vllm': { icon: `/images/ai/local.png`, description: 'vLLM', color: '#666666' },
  'mistral': { icon: `/images/ai/mistral.png`, description: 'Mistral', color: '#FF6F61' },
  'openrouter': { icon: `/images/ai/openrouter.webp`, description: 'OpenRouter', color: '#6467F2' }
};
